import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { type ReactNode } from 'react'
import { useState } from 'react'

import {
  maxCampaignThrottleMps,
  renderTemplate,
  templateSlotLabels,
} from '@aeris/communication'
import {
  Button,
  ConfirmDialog,
  DateField,
  Field,
  HelpHint,
  Input,
  Page,
  PageHeader,
  RadioGroup,
  Section,
  Select,
  Spinner,
  Stepper,
} from '@aeris/ui'

import {
  ProductMultiPicker,
  type SelectedProduct,
} from '../components/product-multi-picker.tsx'
import { WhatsAppPreview } from '../components/whatsapp-preview.tsx'
import {
  ApiError,
  createCampaign,
  fetchChannelAccounts,
  fetchTemplates,
  previewCampaignSegment,
  scheduleCampaign,
} from '../lib/api.ts'

/**
 * CRM → Campanhas → Nova (F060): o assistente que substitui o modal de nove
 * campos.
 *
 * **Por que passo a passo, e por que aqui e não nos modelos.** Um modelo é
 * um texto sendo escrito: quebrar em passos ESCONDE da prévia o que ela
 * mostra, então lá o formulário é uma coluna só. Uma campanha é o contrário
 * — cada passo é uma decisão que FECHA (quem recebe, o que recebe, quando),
 * e a última delas gasta dinheiro de verdade. Ver os dois lados registrados
 * em `docs/features/F066-template-media.md`.
 *
 * ## A contagem morta
 *
 * No modal, a pré-visualização do segmento era um botão opcional cujo
 * resultado **não era invalidado quando o segmento mudava**: dava para ver
 * "1.284 pessoas", trocar o produto, e criar a campanha com o número velho
 * ainda na tela. Aqui, qualquer mudança no segmento apaga a contagem
 * (`resetPreview`), e o passo 1 não avança sem uma contagem VÁLIDA — o
 * número que aparece na confirmação é, por construção, o número daquele
 * segmento.
 *
 * ## O ritmo em português
 *
 * "Mensagens por segundo" é a unidade do gateway, não a pergunta de quem
 * dispara. Vira devagar/normal/rápido sobre o teto da conta
 * (`maxCampaignThrottleMps`, a MESMA função que o servidor usa para
 * recusar), com o número exato atrás de "definir o número na mão" para quem
 * precisar dele.
 */

const STEPS = [
  { id: 'quem', label: 'Quem recebe' },
  { id: 'oque', label: 'O que enviar' },
  { id: 'quando', label: 'Quando e ritmo' },
  { id: 'confirmar', label: 'Confirmar' },
] as const

type SegmentKind = 'produtos' | 'marca'

type Preview = {
  readonly totalMatches: number
  readonly optedOutCount: number
}

const PACE_OPTIONS = [
  {
    value: 'devagar',
    label: 'Devagar',
    description: 'Um quarto do teto. Menos chance de reclamação em massa.',
  },
  {
    value: 'normal',
    label: 'Normal',
    description: 'Metade do teto disponível para campanhas.',
  },
  {
    value: 'rapido',
    label: 'Rápido',
    description: 'O teto inteiro — o atendimento ao vivo segue reservado.',
  },
] as const

type Pace = (typeof PACE_OPTIONS)[number]['value']

const PACE_FRACTION: Record<Pace, number> = {
  devagar: 0.25,
  normal: 0.5,
  rapido: 1,
}

/** O ritmo em mps que um ritmo em português significa, dado o teto da conta. Nunca abaixo de 1. */
const mpsFor = (pace: Pace, ceiling: number): number =>
  Math.max(1, Math.round(ceiling * PACE_FRACTION[pace]))

/** `<input type="date">` devolve `2026-08-15`; o contrato quer um instante. */
const instantFrom = (date: string, endOfDay: boolean): string | null => {
  if (date === '') return null
  const parsed = new Date(`${date}T${endOfDay ? '23:59:59' : '00:00:00'}`)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

export const CrmCampanhasNovaPage = (): ReactNode => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [step, setStep] = useState(0)
  const [formError, setFormError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const [accountId, setAccountId] = useState('')
  const [name, setName] = useState('')
  const [segmentKind, setSegmentKind] = useState<SegmentKind>('produtos')
  const [products, setProducts] = useState<readonly SelectedProduct[]>([])
  const [brand, setBrand] = useState('')
  const [purchasedFrom, setPurchasedFrom] = useState('')
  const [purchasedTo, setPurchasedTo] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)

  const [templateId, setTemplateId] = useState('')

  const [scheduledFor, setScheduledFor] = useState('')
  const [pace, setPace] = useState<Pace>('normal')
  const [manualMps, setManualMps] = useState<number | null>(null)

  const accounts = useQuery({
    queryKey: ['communication-channel-accounts'],
    queryFn: async ({ signal }) => await fetchChannelAccounts(signal),
    // Modelo, campanha e follow-up são da API oficial: o aparelho vinculado
    // não tem modelo e não pode abrir conversa com quem nunca escreveu (F192).
    select: (rows) =>
      rows.filter((account) => account.connection === 'cloud_api'),
  })

  const selectedAccount = (accounts.data ?? []).find(
    (account) => account.id === accountId
  )
  const throttleCeiling =
    selectedAccount === undefined
      ? null
      : maxCampaignThrottleMps(selectedAccount.throughputMps)

  const templates = useQuery({
    queryKey: ['communication-templates', accountId],
    queryFn: async ({ signal }) =>
      await fetchTemplates(accountId === '' ? undefined : accountId, signal),
    enabled: accountId !== '',
  })
  const approvedTemplates = (templates.data ?? []).filter(
    (template) => template.status === 'approved'
  )
  const selectedTemplate = approvedTemplates.find(
    (template) => template.id === templateId
  )

  /**
   * Qualquer mudança no segmento invalida a contagem. Era o defeito do modal
   * anterior: o número ficava na tela depois de o critério mudar.
   */
  const resetPreview = (): void => {
    setPreview(null)
  }

  const segmentBody = {
    productIds:
      segmentKind === 'produtos' ? products.map((product) => product.id) : [],
    productBrand: segmentKind === 'marca' && brand !== '' ? brand : null,
    purchasedFrom: instantFrom(purchasedFrom, false),
    purchasedTo: instantFrom(purchasedTo, true),
  }

  const segmentIsComplete =
    accountId !== '' &&
    (segmentKind === 'produtos' ? products.length > 0 : brand.trim() !== '')

  const countPreview = useMutation({
    mutationFn: async () =>
      await previewCampaignSegment({ accountId, segment: segmentBody }),
    onSuccess: (result) => {
      setFormError(null)
      setPreview(result)
    },
    onError: (cause: unknown) => {
      setFormError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível contar quem este segmento alcança.'
      )
    },
  })

  const effectiveMps =
    manualMps ?? (throttleCeiling === null ? 1 : mpsFor(pace, throttleCeiling))

  const save = useMutation({
    mutationFn: async () => {
      const campaign = await createCampaign({
        accountId,
        templateId,
        name,
        segment: segmentBody,
        throttleMps: effectiveMps,
      })

      // `CampaignCreate` não carrega data: uma campanha nasce SEMPRE em
      // rascunho, e agendar é uma transição de estado com sua própria
      // versão otimista (`CampaignScheduleRequest`, que existia sem
      // interface nenhuma até aqui). Duas chamadas, e não um campo a mais
      // no create, porque agendar tem regra própria no domínio
      // (`canTransitionCampaign`) — embutir isso no create duplicaria a
      // regra em dois caminhos.
      if (scheduledFor !== '') {
        await scheduleCampaign(campaign.id, {
          scheduledFor: new Date(scheduledFor).toISOString(),
          version: campaign.version,
        })
      }
    },
    onSuccess: async () => {
      setFormError(null)
      await queryClient.invalidateQueries({
        queryKey: ['communication-campaigns'],
      })
      await navigate({ to: '/crm-campanhas' })
    },
    onError: (cause: unknown) => {
      setFormError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível criar esta campanha.'
      )
    },
  })

  /** Quantas pessoas de fato vão receber — o total menos quem já pediu para sair. É este número que aparece na confirmação. */
  const willReceive =
    preview === null ? 0 : preview.totalMatches - preview.optedOutCount

  const canAdvance =
    step === 0
      ? segmentIsComplete && preview !== null && name.trim() !== ''
      : step === 1
        ? selectedTemplate !== undefined
        : true

  const rendered =
    selectedTemplate === undefined
      ? null
      : renderTemplate({
          structure: selectedTemplate.structure,
          values: [],
        })

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link
            to="/crm-campanhas"
            className="text-(--brand-primary) hover:underline"
          >
            ← Campanhas
          </Link>
        }
        title="Nova campanha"
        description="Quatro passos. Nada é enviado até você confirmar no último, com o número de pessoas na frente."
      />

      <Stepper
        steps={STEPS}
        currentIndex={step}
        onStepClick={(index) => {
          setStep(index)
        }}
      />

      {step === 0 ? (
        <Section
          title="Quem recebe"
          description="O critério fica congelado no momento da criação — mudar o produto depois não muda quem já recebeu."
        >
          <Field label="Conta" required>
            {(field) => (
              <Select
                {...field}
                value={accountId}
                disabled={accounts.isPending}
                onChange={(event) => {
                  setAccountId(event.target.value)
                  setTemplateId('')
                  resetPreview()
                }}
              >
                <option value="">Escolha uma conta</option>
                {(accounts.data ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.displayName}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Nome da campanha" required>
            {(field) => (
              <Input
                {...field}
                value={name}
                placeholder="Reajuste de preço — ar-condicionado"
                onChange={(event) => {
                  setName(event.target.value)
                }}
              />
            )}
          </Field>

          <Field label="Alcança quem comprou" required>
            {(field) => (
              <RadioGroup
                {...field}
                value={segmentKind}
                onValueChange={(next) => {
                  setSegmentKind(next)
                  resetPreview()
                }}
                orientation="horizontal"
                options={[
                  { value: 'produtos', label: 'Produtos específicos' },
                  { value: 'marca', label: 'Qualquer produto de uma marca' },
                ]}
              />
            )}
          </Field>

          {segmentKind === 'produtos' ? (
            <Field
              label="Produtos"
              required
              hint="Vários — quem comprou QUALQUER um deles entra. Uma venda com dois produtos da lista continua sendo uma pessoa só."
            >
              {() => (
                <ProductMultiPicker
                  value={products}
                  queryKeyPrefix="campaign-product-picker"
                  onChange={(next) => {
                    setProducts(next)
                    resetPreview()
                  }}
                />
              )}
            </Field>
          ) : (
            <Field
              label="Marca"
              required
              hint="O Aeris não tem tabela de categoria — casa com a marca do produto, sem diferenciar maiúsculas."
            >
              {(field) => (
                <Input
                  {...field}
                  value={brand}
                  placeholder="Samsung"
                  onChange={(event) => {
                    setBrand(event.target.value)
                    resetPreview()
                  }}
                />
              )}
            </Field>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Comprou a partir de"
              hint="Opcional — em branco não restringe."
            >
              {(field) => (
                <DateField
                  {...field}
                  value={purchasedFrom}
                  onChange={(event) => {
                    setPurchasedFrom(event.target.value)
                    resetPreview()
                  }}
                />
              )}
            </Field>
            <Field label="Até">
              {(field) => (
                <DateField
                  {...field}
                  value={purchasedTo}
                  onChange={(event) => {
                    setPurchasedTo(event.target.value)
                    resetPreview()
                  }}
                />
              )}
            </Field>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-(--radius-md) border border-(--border-default) bg-(--surface-sunken) p-3">
            <p className="text-(length:--text-sm)">
              {preview === null ? (
                <span className="text-(--text-muted)">
                  Conte quantas pessoas este segmento alcança para seguir.
                </span>
              ) : (
                <>
                  <strong>{preview.totalMatches}</strong> pessoas no segmento —{' '}
                  <strong>{preview.optedOutCount}</strong> já pediram para sair.{' '}
                  <strong>{willReceive}</strong> vão receber.
                </>
              )}
            </p>
            <Button
              type="button"
              variant="secondary"
              loading={countPreview.isPending}
              disabled={!segmentIsComplete}
              onClick={() => {
                countPreview.mutate()
              }}
            >
              Contar
            </Button>
          </div>
        </Section>
      ) : null}

      {step === 1 ? (
        <Section
          title="O que enviar"
          description="Fora da janela de 24 horas só sai modelo aprovado pela Meta — e agora dá para ver o texto antes de escolher."
        >
          <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
            <Field label="Modelo aprovado" required>
              {(field) => (
                <Select
                  {...field}
                  value={templateId}
                  disabled={templates.isPending}
                  onChange={(event) => {
                    setTemplateId(event.target.value)
                  }}
                >
                  <option value="">Escolha um modelo</option>
                  {approvedTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.metaTemplateName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <aside className="lg:sticky lg:top-4 lg:self-start">
              {templates.isPending ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : rendered === null || selectedTemplate === undefined ? (
                <p className="rounded-(--radius-md) bg-(--surface-sunken) p-4 text-(length:--text-sm) text-(--text-muted)">
                  Escolha um modelo para ver como a mensagem chega.
                </p>
              ) : (
                <WhatsAppPreview
                  rendered={rendered}
                  at={new Date()}
                  slotLabels={templateSlotLabels({
                    structure: selectedTemplate.structure,
                    variables: selectedTemplate.variables,
                  })}
                />
              )}
            </aside>
          </div>

          {approvedTemplates.length === 0 && !templates.isPending ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Nenhum modelo aprovado nesta conta ainda.{' '}
              <Link
                to="/atendimento-modelos"
                className="text-(--brand-primary) hover:underline"
              >
                Criar um modelo
              </Link>{' '}
              — a análise da Meta leva alguns dias.
            </p>
          ) : null}
        </Section>
      ) : null}

      {step === 2 ? (
        <Section
          title="Quando e a que ritmo"
          description="Em branco, a campanha nasce em rascunho e só sai quando você mandar iniciar."
        >
          <Field
            label="Agendar para"
            hint="Opcional. Com data e hora, ela sai sozinha na hora marcada."
          >
            {(field) => (
              <DateField
                {...field}
                type="datetime-local"
                value={scheduledFor}
                onChange={(event) => {
                  setScheduledFor(event.target.value)
                }}
              />
            )}
          </Field>

          <Field
            label="Ritmo"
            required
            hint={
              throttleCeiling === null
                ? 'Escolha a conta para ver o teto disponível.'
                : `${String(effectiveMps)} mensagens por segundo. O teto desta conta é ${String(selectedAccount?.throughputMps ?? 0)}, e uma margem fica reservada para quem está conversando agora.`
            }
            help={({ id }) => (
              <HelpHint label="ritmo da campanha" id={id}>
                O teto da conta é compartilhado com o atendimento ao vivo — por
                isso nunca dá para pedir o total: uma margem fica reservada para
                quem está conversando agora, e uma campanha não pode engasgar
                isso.
              </HelpHint>
            )}
          >
            {(field) => (
              <RadioGroup
                {...field}
                value={manualMps === null ? pace : null}
                onValueChange={(next) => {
                  setPace(next)
                  setManualMps(null)
                }}
                options={PACE_OPTIONS}
              />
            )}
          </Field>

          {manualMps === null ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-start"
              disabled={throttleCeiling === null}
              onClick={() => {
                setManualMps(
                  throttleCeiling === null ? 1 : mpsFor(pace, throttleCeiling)
                )
              }}
            >
              Definir o número na mão
            </Button>
          ) : (
            <Field label="Mensagens por segundo" required>
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  max={throttleCeiling ?? 1000}
                  value={manualMps}
                  onChange={(event) => {
                    setManualMps(Number(event.target.value))
                  }}
                />
              )}
            </Field>
          )}
        </Section>
      ) : null}

      {step === 3 ? (
        <Section
          title="Confirmar"
          description="Nada foi enviado ainda. A campanha nasce em rascunho (ou agendada) e você ainda pode cancelar."
        >
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-(length:--text-xs) text-(--text-muted)">
                Nome
              </dt>
              <dd className="text-(length:--text-sm)">{name}</dd>
            </div>
            <div>
              <dt className="text-(length:--text-xs) text-(--text-muted)">
                Quem recebe
              </dt>
              <dd className="text-(length:--text-sm)">
                {segmentKind === 'produtos'
                  ? products.map((product) => product.name).join(', ')
                  : `Marca ${brand}`}
              </dd>
            </div>
            <div>
              <dt className="text-(length:--text-xs) text-(--text-muted)">
                Modelo
              </dt>
              <dd className="text-(length:--text-sm)">
                {selectedTemplate?.metaTemplateName ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="text-(length:--text-xs) text-(--text-muted)">
                Ritmo
              </dt>
              <dd className="text-(length:--text-sm)">
                {effectiveMps} mensagens por segundo
              </dd>
            </div>
            <div>
              <dt className="text-(length:--text-xs) text-(--text-muted)">
                Quando
              </dt>
              <dd className="text-(length:--text-sm)">
                {scheduledFor === ''
                  ? 'Rascunho — sai quando você mandar'
                  : new Date(scheduledFor).toLocaleString('pt-BR')}
              </dd>
            </div>
            <div>
              <dt className="text-(length:--text-xs) text-(--text-muted)">
                Vão receber
              </dt>
              <dd className="text-(length:--text-sm) font-(--weight-semibold)">
                {willReceive} pessoas
              </dd>
            </div>
          </dl>
        </Section>
      ) : null}

      {formError === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {formError}
        </p>
      )}

      <div className="flex justify-between gap-2">
        {step === 0 ? (
          <Button asChild variant="ghost">
            <Link to="/crm-campanhas">Cancelar</Link>
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setStep(step - 1)
            }}
          >
            Voltar
          </Button>
        )}

        {step < STEPS.length - 1 ? (
          <Button
            type="button"
            variant="primary"
            disabled={!canAdvance}
            onClick={() => {
              setStep(step + 1)
            }}
          >
            Continuar
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            loading={save.isPending}
            onClick={() => {
              setConfirming(true)
            }}
          >
            Criar campanha
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Criar campanha para ${String(willReceive)} pessoas?`}
        description={
          scheduledFor === ''
            ? 'Ela nasce em rascunho: nenhuma mensagem sai até você mandar iniciar na lista.'
            : `Ela vai sair sozinha em ${new Date(scheduledFor).toLocaleString('pt-BR')}. Cada mensagem enviada é cobrada pela Meta.`
        }
        confirmLabel="Criar"
        loading={save.isPending}
        onConfirm={() => {
          setConfirming(false)
          save.mutate()
        }}
      />
    </Page>
  )
}
